import React, { useEffect, useState, useCallback } from "react";
import { api, fmtIST } from "../lib/api";
import { useAuth } from "../lib/auth";
import Shell, { PageHeader } from "../components/Shell";
import { FunnelSimple, CaretLeft, CaretRight, ArrowDown, ArrowUp, MagnifyingGlass, Package } from "@phosphor-icons/react";

const ENTITIES = [
  ["", "All"], ["inventory", "Stock movements"], ["sale", "Invoices"], ["credit_note", "Credit notes"],
  ["dispatch", "Delivery challans"], ["inward", "Inwards"], ["sku", "SKU master"], ["sku_alias", "SKU aliases"],
  ["reco", "Reconciliation"], ["user", "Users"], ["store", "Stores"], ["settings", "Settings"],
  ["customer", "Customers"], ["day_close", "Day close"], ["cash_submission", "Cash handover"],
];
const ACTIONS = ["", "create", "update", "delete", "apply", "cancel", "sale", "sale_edit", "sale_cancel", "inward", "reco_apply", "bulk_import", "manual_adjust", "credit_note", "dispatch_delete"];

const ACTION_LABEL = {
  sale: "Sold", sale_edit: "Invoice edited", sale_cancel: "Invoice cancelled", inward: "Inward", reco_apply: "Reco adjust",
  bulk_import: "Bulk import", manual_adjust: "Manual adjust", credit_note: "Return (CN)", dispatch_delete: "Challan deleted",
  create: "Created", update: "Updated", delete: "Deleted", apply: "Applied", cancel: "Cancelled",
};

const actionColor = (a) => ({
  create: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-blue-50 text-blue-700 border-blue-200",
  manual_adjust: "bg-amber-50 text-amber-700 border-amber-200",
  credit_note: "bg-amber-50 text-amber-700 border-amber-200",
  delete: "bg-red-50 text-red-700 border-red-200",
  cancel: "bg-red-50 text-red-700 border-red-200",
  dispatch_delete: "bg-red-50 text-red-700 border-red-200",
  apply: "bg-amber-50 text-amber-700 border-amber-200",
  sale: "bg-indigo-50 text-indigo-700 border-indigo-200",
  sale_edit: "bg-blue-50 text-blue-700 border-blue-200",
  sale_cancel: "bg-red-50 text-red-700 border-red-200",
  inward: "bg-emerald-50 text-emerald-700 border-emerald-200",
  reco_apply: "bg-amber-50 text-amber-700 border-amber-200",
  bulk_import: "bg-purple-50 text-purple-700 border-purple-200",
}[a] || "bg-slate-100 text-slate-700 border-slate-200");

const PAGE_SIZE = 50;

function StockBadge({ a }) {
  const delta = Number(a.extra?.delta || 0);
  if (!a.direction) return null;
  const isIn = a.direction === "in";
  return (
    <span data-testid={`stock-dir-${a.id}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest ${isIn ? "bg-emerald-500 text-white" : "bg-red-500 text-white"}`}>
      {isIn ? <ArrowDown size={10} weight="bold" /> : <ArrowUp size={10} weight="bold" />}
      Stock {isIn ? "In" : "Out"} {delta > 0 ? "+" : ""}{Number.isInteger(delta) ? delta : delta.toFixed(2)}
    </span>
  );
}

function Thumb({ src, isNew }) {
  if (src) return <img src={src} alt="" className="w-11 h-11 rounded-md object-cover border border-slate-200 shrink-0" />;
  return <div className={`w-11 h-11 rounded-md border flex items-center justify-center shrink-0 ${isNew ? "bg-amber-50 border-amber-200 text-amber-400" : "bg-slate-50 border-slate-200 text-slate-300"}`}><Package size={18} /></div>;
}

export default function Audit() {
  const { stores } = useAuth();
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [direction, setDirection] = useState("");
  const [storeId, setStoreId] = useState("");
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ items: [], total: 0, skip: 0, limit: PAGE_SIZE });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (entity) params.set("entity_type", entity);
      if (action) params.set("action", action);
      if (direction) params.set("direction", direction);
      if (storeId) params.set("store_id", storeId);
      if (q) params.set("q", q);
      if (start) params.set("start", start);
      if (end) params.set("end", `${end}T23:59:59`);
      params.set("skip", String(skip));
      params.set("limit", String(PAGE_SIZE));
      const r = await api.get(`/audit?${params.toString()}`);
      setData(r.data);
    } finally { setLoading(false); }
  }, [entity, action, direction, storeId, q, start, end, skip]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSkip(0); }, [entity, action, direction, storeId, q, start, end]);

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;
  const hasFilters = start || end || entity || action || direction || storeId || q;
  const sel = "h-10 px-3 border border-slate-300 rounded-md text-sm bg-white";

  return (
    <Shell>
      <PageHeader title="Audit Log" subtitle="Who did what, when — stock in/out per SKU with the invoice or challan behind it, invoice edits, SKU & user changes." testId="audit-header" />
      <div className="p-8 space-y-4">
        <div className="bg-white border border-slate-200 rounded-md p-3 space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <FunnelSimple size={16} className="text-slate-500 shrink-0" />
            <div className="inline-flex bg-slate-50 border border-slate-200 rounded-md p-1" data-testid="audit-direction">
              {[["", "All events"], ["in", "Stock In"], ["out", "Stock Out"]].map(([k, l]) => (
                <button key={k} onClick={() => { setDirection(k); if (k) setEntity("inventory"); }} data-testid={`audit-dir-${k || "all"}`} className={`px-3 h-8 text-xs font-semibold rounded-sm ${direction === k ? (k === "in" ? "bg-emerald-500 text-white" : k === "out" ? "bg-red-500 text-white" : "bg-slate-900 text-white") : "text-slate-700 hover:bg-white"}`}>{l}</button>
              ))}
            </div>
            <select value={entity} onChange={(e) => { setEntity(e.target.value); if (e.target.value !== "inventory") setDirection(""); }} data-testid="audit-entity" className={sel}>
              {ENTITIES.map(([k, l]) => <option key={k || "all"} value={k}>{k ? `Type: ${l}` : "Type: All"}</option>)}
            </select>
            <select value={action} onChange={(e) => setAction(e.target.value)} data-testid="audit-action" className={sel}>
              {ACTIONS.map((a) => <option key={a || "all"} value={a}>{a ? `Action: ${ACTION_LABEL[a] || a}` : "Action: All"}</option>)}
            </select>
            {stores.length > 1 && (
              <select value={storeId} onChange={(e) => setStoreId(e.target.value)} data-testid="audit-store" className={sel}>
                <option value="">Store: All</option>
                {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
            <div className="ml-auto text-xs text-slate-500 font-mono-num" data-testid="audit-total">{data.total.toLocaleString("en-IN")} entries</div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <form onSubmit={(e) => { e.preventDefault(); setQ(qInput.trim()); }} className="relative flex-1 min-w-[260px] max-w-md">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Search SKU, product, invoice / challan no., user…" data-testid="audit-search" className="w-full h-10 pl-8 pr-3 border border-slate-300 rounded-md text-sm" />
            </form>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">From</span>
              <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="audit-start" className="h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">To</span>
              <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="audit-end" className="h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
            </div>
            {hasFilters && (
              <button onClick={() => { setStart(""); setEnd(""); setEntity(""); setAction(""); setDirection(""); setStoreId(""); setQ(""); setQInput(""); }} data-testid="audit-clear" className="h-10 px-3 text-xs font-semibold text-slate-700 hover:bg-slate-100 rounded-md">Clear</button>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-3 font-semibold w-40">When (IST)</th>
                <th className="text-left px-4 py-3 font-semibold w-44">By</th>
                <th className="text-left px-4 py-3 font-semibold">What happened</th>
                <th className="text-left px-4 py-3 font-semibold w-44">Reference</th>
                <th className="text-right px-4 py-3 font-semibold w-24">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="text-center py-12 text-slate-400">Loading…</td></tr>}
              {!loading && data.items.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-slate-400">No audit entries match.</td></tr>}
              {!loading && data.items.map((a) => {
                const isInv = a.entity_type === "inventory";
                const isSku = a.entity_type === "sku" || a.entity_type === "sku_alias";
                const sku = isInv ? (a.extra?.sku || (a.entity_id || "").split(":").pop()) : isSku ? (a.extra?.sku || a.entity_id) : "";
                return (
                  <React.Fragment key={a.id}>
                    <tr className="border-b border-slate-100 hover:bg-slate-50 align-top" data-testid={`audit-row-${a.id}`}>
                      <td className="px-4 py-3 font-mono-num text-xs text-slate-600">{fmtIST(a.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-xs truncate max-w-[160px]" title={a.user_email}>{a.user_email}</div>
                        <div className="text-[10px] text-slate-500 uppercase tracking-widest">{(a.user_role || "").replace("_", " ")}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-3">
                          {(isInv || isSku) && <Thumb src={a.thumbnail} isNew={a.action === "create"} />}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              {isInv && a.direction ? <StockBadge a={a} /> : (
                                <span className={`inline-block px-2 py-0.5 border rounded-full text-[10px] uppercase tracking-widest font-bold ${actionColor(a.action)}`}>{ACTION_LABEL[a.action] || a.action}</span>
                              )}
                              <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">{ENTITIES.find(([k]) => k === a.entity_type)?.[1] || a.entity_type}</span>
                              {a.store_name && <span className="text-[10px] text-slate-500">· {a.store_name}</span>}
                            </div>
                            {(isInv || isSku) && sku && (
                              <div className="mt-1 flex items-baseline gap-2 flex-wrap">
                                <span className="font-mono-num font-semibold text-slate-900" data-testid={`audit-sku-${a.id}`}>{sku}</span>
                                {a.sku_name && <span className="text-xs text-slate-600 truncate max-w-[360px]">{a.sku_name}</span>}
                                {isInv && a.after?.qty != null && a.direction && (
                                  <span className="text-[11px] text-slate-500 font-mono-num">{a.before?.qty} → <b className="text-slate-800">{a.after.qty}</b> in stock</span>
                                )}
                              </div>
                            )}
                            <div className="text-xs text-slate-600 mt-0.5" data-testid={`audit-summary-${a.id}`}>{a.summary}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono-num text-slate-700" data-testid={`audit-ref-${a.id}`}>{a.ref_label || <span className="text-slate-300">—</span>}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setExpanded(expanded === a.id ? null : a.id)} data-testid={`audit-toggle-${a.id}`} className="text-xs font-semibold text-slate-700 hover:text-slate-900">
                          {expanded === a.id ? "Hide" : "View diff"}
                        </button>
                      </td>
                    </tr>
                    {expanded === a.id && (
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <td colSpan={5} className="p-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-[10px] uppercase tracking-widest font-semibold text-red-600 mb-1">Before</div>
                              <pre className="bg-white border border-slate-200 rounded-md p-3 text-[11px] overflow-auto max-h-72 font-mono-num">{a.before ? JSON.stringify(a.before, null, 2) : "—"}</pre>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-600 mb-1">After</div>
                              <pre className="bg-white border border-slate-200 rounded-md p-3 text-[11px] overflow-auto max-h-72 font-mono-num">{a.after ? JSON.stringify(a.after, null, 2) : "—"}</pre>
                            </div>
                          </div>
                          {a.extra && Object.keys(a.extra).length > 0 && (
                            <div className="mt-3">
                              <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1">Extra · entity id <span className="font-mono-num normal-case">{a.entity_id}</span></div>
                              <pre className="bg-white border border-slate-200 rounded-md p-3 text-[11px] overflow-auto font-mono-num">{JSON.stringify(a.extra, null, 2)}</pre>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 text-xs text-slate-500">
            <div>Page <b>{currentPage}</b> of <b>{totalPages}</b> · <span className="font-mono-num">{data.total}</span> entries</div>
            <div className="flex gap-2">
              <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} data-testid="audit-prev" className="h-8 px-3 border border-slate-300 rounded-md disabled:opacity-40 inline-flex items-center gap-1"><CaretLeft size={12} />Prev</button>
              <button disabled={skip + PAGE_SIZE >= data.total} onClick={() => setSkip(skip + PAGE_SIZE)} data-testid="audit-next" className="h-8 px-3 border border-slate-300 rounded-md disabled:opacity-40 inline-flex items-center gap-1">Next<CaretRight size={12} /></button>
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
}
